import React, { useEffect, useState } from 'react';
import { Icon } from '../components/sp-primitives';
import { Modal } from '../components/sp-overlays';
import { PShell, useApp } from '../components/sp-shell';
import { isAbortError } from '../services/apiClient';

export function PCompanyForm() {
  const app = useApp();
  const company = app.companies.find((c) => c.id === app.params?.companyId);
  const isEdit = !!company;
  const canManageCompany = Array.isArray(app.currentUser?.permissions) && app.currentUser.permissions.includes('Administrador');
  const readOnlyProfile = isEdit && !canManageCompany;
  const formatCnpj = (value) => {
    const d = String(value || '').replace(/\D/g, '').slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  };
  const cnpjDigits = (value) => String(value || '').replace(/\D/g, '').slice(0, 14);
  const [name, setName] = useState(company?.name || '');
  const [document, setDocument] = useState(formatCnpj(company?.document || ''));
  const [subdomain, setSubdomain] = useState('');
  const [apiUrl, setApiUrl] = useState('');
  const [apiVersion, setApiVersion] = useState(() => String(company?.sienge_api_version || 'v1').replace(/[^a-zA-Z0-9]/g, '').slice(0, 5) || 'v1');
  const [apiUser, setApiUser] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [status, setStatus] = useState(company?.status || 'active');
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [connectionTest, setConnectionTest] = useState({ status: 'idle', message: '' });
  const credsConfigured = company?.sienge_credentials_configured === true;
  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    app.navigate('companies');
  };

  useEffect(() => {
    setName(company?.name || '');
    setDocument(formatCnpj(company?.document || ''));
    setStatus(company?.status || 'active');
    setSubdomain(company?.sienge_subdomain || '');
    setApiUrl(company?.sienge_api_url || '');
    setApiVersion(String(company?.sienge_api_version || 'v1').replace(/[^a-zA-Z0-9]/g, '').slice(0, 5) || 'v1');
    setApiUser(company?.sienge_api_user || '');
    setApiToken('');
    setConnectionTest({ status: 'idle', message: '' });
  }, [company?.id, company?.name, company?.document, company?.status, company?.sienge_subdomain, company?.sienge_api_url, company?.sienge_api_version, company?.sienge_api_user, company?.sienge_credentials_configured]);

  const runConnectionTest = async () => {
    if (readOnlyProfile) {
      app.toast('info', 'Somente leitura', 'Apenas usuários com permissão Administrador podem alterar ou testar credenciais da empresa.');
      return;
    }
    const sub = subdomain.trim();
    const surl = apiUrl.trim();
    const sver = apiVersion.replace(/[^a-zA-Z0-9]/g, '').slice(0, 5);
    const suser = apiUser.trim();
    const stoken = apiToken.trim();
    const useSavedToken = isEdit && company?.id && credsConfigured && !stoken;

    if (!sub || !suser) {
      app.toast('error', 'Credenciais incompletas', 'Informe subdomínio e usuário da API Sienge para testar.');
      return;
    }
    if (!sver) {
      app.toast('error', 'Versão obrigatória', 'Informe a versão da API (1 a 5 caracteres, letras ou números, ex.: v1).');
      return;
    }
    if (!useSavedToken && !surl) {
      app.toast('error', 'URL da API obrigatória', 'Informe a URL da API Sienge para testar.');
      return;
    }
    if (!useSavedToken && !stoken) {
      app.toast('error', 'Senha obrigatória', 'Informe a senha do usuário API para testar. Na edição, se já houver senha salva, pode deixar em branco para testar com a senha atual.');
      return;
    }

    setConnectionTest({ status: 'testing', message: 'Consultando a Sienge…' });
    try {
      let res;
      if (useSavedToken) {
        res = await app.testCompanySiengeConnection(company.id, {
          sienge_subdomain: sub,
          sienge_api_url: surl,
          sienge_api_version: sver,
          sienge_api_user: suser,
        });
      } else {
        res = await app.testSiengeCredentialsPreview({
          sienge_subdomain: sub,
          sienge_api_url: surl,
          sienge_api_version: sver,
          sienge_api_user: suser,
          sienge_api_token: stoken,
        });
      }
      const msg = res?.message || 'Conexão OK.';
      setConnectionTest({ status: 'ok', message: msg });
      app.toast('success', 'Usuário API Sienge', msg);
    } catch (err) {
      const msg = err.message || 'Falha ao testar a conexão.';
      setConnectionTest({ status: 'error', message: msg });
      app.toast('error', 'Usuário API Sienge', msg);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canManageCompany) {
      app.toast('error', 'Sem permissão', 'Apenas usuários com permissão Administrador podem alterar cadastros de empresas.');
      return;
    }
    const documentOnlyDigits = cnpjDigits(document);
    const sub = subdomain.trim();
    const surl = apiUrl.trim();
    const sver = apiVersion.replace(/[^a-zA-Z0-9]/g, '').slice(0, 5);
    const suser = apiUser.trim();
    const stoken = apiToken.trim();
    if (!sub || !suser || !surl || !sver) {
      app.toast('error', 'Credenciais obrigatórias', 'Informe o subdomínio, a URL da API, a versão (ex.: v1), o usuário da API e a senha quando necessário.');
      return;
    }
    if (!isEdit && !stoken) {
      app.toast('error', 'Credenciais obrigatórias', 'Informe a senha do usuário API.');
      return;
    }
    if (isEdit && !credsConfigured && !stoken) {
      app.toast('error', 'Credenciais obrigatórias', 'Informe a senha do usuário API.');
      return;
    }
    if (documentOnlyDigits.length !== 14) {
      app.toast('error', 'CNPJ inválido', 'Informe um CNPJ com 14 dígitos.');
      return;
    }
    setSaving(true);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 25000);
    try {
      if (isEdit) {
        const payload = { name, document: documentOnlyDigits, status, sienge_subdomain: sub, sienge_api_url: surl, sienge_api_version: sver, sienge_api_user: suser };
        if (stoken) payload.sienge_api_token = stoken;
        await app.updateCompany(company.id, payload, { signal: controller.signal });
        app.toast('success', 'Empresa atualizada', `${name} foi atualizada com sucesso.`);
      } else {
        const createdCompany = await app.createCompany({
          name,
          document: documentOnlyDigits,
          status,
          sienge_subdomain: sub,
          sienge_api_url: surl,
          sienge_api_version: sver,
          sienge_api_user: suser,
          sienge_api_token: stoken,
        }, { signal: controller.signal });
        const createdCompanyId = Number(createdCompany?.id || 0);
        if (createdCompanyId > 0) {
          const autoSync = createdCompany?.auto_sync || null;
          if (autoSync) {
            const failed = [];
            if (autoSync.creditors?.error) failed.push(`Credores (${autoSync.creditors.error})`);
            if (autoSync.companies?.error) failed.push(`Companias (${autoSync.companies.error})`);
            if (autoSync.cost_centers?.error) failed.push(`Centros de Custo (${autoSync.cost_centers.error})`);
            if (failed.length === 0) {
              app.toast('info', 'Sincronizações automáticas', 'Backend enfileirou Credores, Companias e Centros de Custo.');
            } else {
              app.toast('error', 'Sincronizações automáticas', `Backend não enfileirou: ${failed.join(' | ')}`);
            }
          }
        }
        app.toast('success', 'Empresa cadastrada', `${name} foi registrada com sucesso.`);
      }
      app.navigate('companies');
    } catch (error) {
      if (isAbortError(error)) {
        app.toast('error', 'Tempo esgotado ao salvar', 'A API demorou mais que o esperado para responder. Tente novamente e, se persistir, valide o backend.');
        return;
      }
      app.toast('error', isEdit ? 'Falha ao atualizar empresa' : 'Falha ao cadastrar empresa', error.message || 'Verifique os dados e tente novamente.');
    } finally {
      window.clearTimeout(timeoutId);
      setSaving(false);
    }
  };

  const openDeleteModal = () => {
    if (!canManageCompany) {
      app.toast('error', 'Sem permissão', 'Apenas usuários com permissão Administrador podem alterar cadastros de empresas.');
      return;
    }
    if (!isEdit) {
      app.navigate('companies');
      return;
    }
    setDeletePassword('');
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!canManageCompany) {
      app.toast('error', 'Sem permissão', 'Apenas usuários com permissão Administrador podem alterar cadastros de empresas.');
      return;
    }
    if (!company?.id) return;
    if (!deletePassword.trim()) {
      app.toast('error', 'Senha obrigatória', 'Informe sua senha para confirmar a exclusão.');
      return;
    }
    setDeleting(true);
    try {
      await app.deleteCompany(company.id, deletePassword);
      setConfirmDeleteOpen(false);
      app.toast('success', 'Empresa excluída', `${company.name} foi removida definitivamente.`);
      app.navigate('companies');
    } catch (error) {
      app.toast('error', 'Falha ao excluir empresa', error.message || 'Não foi possível excluir a empresa.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PShell active="companies">
      <button type="button" className="sp-btn sp-btn-ghost sp-form-back" onClick={goBack}>← Voltar</button>
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">{isEdit ? 'Editar empresa' : 'Nova empresa'}</div>
          <h1 className="sp-screen-head__title">Registrar integração Sienge</h1>
          <div className="sp-screen-head__meta">Configure dados de identificação e credenciais de API.</div>
          {readOnlyProfile && (
            <div className="sp-screen-head__meta">Visualização habilitada. Apenas Administrador pode alterar estes dados.</div>
          )}
        </div>
      </div>

      <form onSubmit={submit}>
        <div className="sp-company-form-grid">
          <div className="sp-form-stack">
            <div className="sp-card sp-form-section">
              <div className="sp-form-step-head">
                <div className="sp-form-step-num">1</div>
                <div className="sp-form-step-title">Dados da empresa</div>
              </div>
              <div className="sp-form-grid-3">
                <div>
                  <label className="sp-caps sp-label">Status</label>
                  <select className="sp-input" value={status} onChange={(e) => setStatus(e.target.value)} disabled={readOnlyProfile}>
                    <option value="active">Ativa</option>
                    <option value="inactive">Inativa</option>
                  </select>
                </div>
                <div>
                  <label className="sp-caps sp-label">Nome / Razão social</label>
                  <input className="sp-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Construtora Exemplo S.A." required disabled={readOnlyProfile} />
                </div>
                <div>
                  <label className="sp-caps sp-label">CNPJ</label>
                  <input className="sp-input sp-mono" value={document} onChange={(e) => setDocument(formatCnpj(e.target.value))} placeholder="00.000.000/0001-00" required disabled={readOnlyProfile} />
                </div>
              </div>
            </div>

            <div className="sp-card sp-form-section">
              <div className="sp-form-step-head">
                <div className="sp-form-step-num">2</div>
                <div className="sp-form-step-title">Credenciais da API Sienge</div>
              </div>
              <div className="sp-field-block">
                <label className="sp-caps sp-label">Subdomínio <span className="sp-label-req">obrigatório</span></label>
                <div className="sp-input-group">
                  <input className="sp-input" value={subdomain} onChange={(e) => setSubdomain(e.target.value)} placeholder="minhaempresa" required disabled={readOnlyProfile} />
                  <span className="sp-input-group__suffix">.sienge.com.br</span>
                </div>
              </div>
              <div className="sp-field-block">
                <label className="sp-caps sp-label">URL API <span className="sp-label-req">obrigatório</span></label>
                <input
                  className="sp-input sp-mono"
                  type="url"
                  value={apiUrl}
                  onChange={(e) => setApiUrl(e.target.value)}
                  placeholder="https://api.sienge.com.br"
                  required
                  disabled={readOnlyProfile}
                />
                <div className="sp-field-hint">
                  Somente o host (ex.: <span className="sp-mono">https://api.sienge.com.br</span> ou <span className="sp-mono">http://127.0.0.1:4010</span>).
                  O Sienge Pay monta <span className="sp-mono">{'{host}/{subdomínio}/public/api/{versão}/…'}</span> usando o subdomínio e a versão abaixo.
                </div>
              </div>
              <div className="sp-field-block">
                <label className="sp-caps sp-label">Versão <span className="sp-label-req">obrigatório</span></label>
                <input
                  className="sp-input sp-mono sp-input-version"
                  value={apiVersion}
                  onChange={(e) => setApiVersion(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 5))}
                  placeholder="v1"
                  maxLength={5}
                  inputMode="text"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  title="Até 5 letras ou números (ex.: v1)"
                  required
                  disabled={readOnlyProfile}
                />
                <div className="sp-field-hint">Segmento da URL antes de /trademarks (ex.: v1). Até 5 caracteres, só letras ou números.</div>
              </div>
              <div className="sp-form-grid-2">
                <div>
                  <label className="sp-caps sp-label">Usuário API <span className="sp-label-req">obrigatório</span></label>
                  <input className="sp-input sp-mono" value={apiUser} onChange={(e) => setApiUser(e.target.value)} placeholder="usuario_api" required disabled={readOnlyProfile} />
                </div>
                <div>
                  <label className="sp-caps sp-label">
                    Usuario API Senha <span className="sp-label-req">{isEdit && credsConfigured ? 'obrigatório só se for alterar' : 'obrigatório'}</span>
                  </label>
                  <input
                    className="sp-input sp-mono"
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    type="password"
                    placeholder={isEdit && credsConfigured ? 'Deixe em branco para manter a senha atual' : '••••••••••••••••'}
                    required={!isEdit || !credsConfigured}
                    disabled={readOnlyProfile}
                  />
                </div>
              </div>
              <div className="sp-company-api-test-row">
                <button
                  type="button"
                  className="sp-btn sp-btn-outline"
                  onClick={runConnectionTest}
                  disabled={saving || connectionTest.status === 'testing' || readOnlyProfile}
                >
                  {connectionTest.status === 'testing' ? 'Testando…' : 'Testar credenciais da API'}
                </button>
                <span className="sp-company-api-test-row__hint">Valida URL, versão, subdomínio, usuário e senha contra a Sienge (sem salvar).</span>
              </div>
              {!isEdit && (
                <div className="sp-api-warn-banner">
                  <Icon name="alert" size={14} />
                  <div className="sp-api-warn-banner__text">
                    Subdomínio, URL da API, versão, usuário e senha do usuário API são obrigatórios para registrar a empresa. Use o botão acima para validar antes de salvar.
                  </div>
                </div>
              )}
              {isEdit && !credsConfigured && (
                <div className="sp-api-warn-banner">
                  <Icon name="alert" size={14} />
                  <div className="sp-api-warn-banner__text">
                    Ainda não há credenciais salvas para esta empresa. Preencha os campos e use o botão acima para testar; depois salve para persistir.
                  </div>
                </div>
              )}
              {isEdit && credsConfigured && connectionTest.status === 'idle' && (
                <div className="sp-api-neut-banner">
                  <Icon name="shield" size={14} />
                  <div className="sp-api-neut-banner__text">
                    Credenciais já salvas. Com a senha do usuário API em branco, o teste usa a senha armazenada; com senha preenchida, testa os valores do formulário antes de salvar.
                  </div>
                </div>
              )}
              {connectionTest.status === 'testing' && (
                <div className="sp-api-neut-banner">
                  <Icon name="clock" size={14} />
                  <div className="sp-api-neut-banner__text">{connectionTest.message}</div>
                </div>
              )}
              {connectionTest.status === 'ok' && (
                <div className="sp-api-ok-banner">
                  <Icon name="check" size={14} />
                  <div className="sp-api-ok-banner__text">{connectionTest.message}</div>
                </div>
              )}
              {connectionTest.status === 'error' && (
                <div className="sp-api-err-banner">
                  <Icon name="alert" size={14} />
                  <div className="sp-api-err-banner__text">{connectionTest.message}</div>
                </div>
              )}
            </div>
          </div>

        </div>

        <div className="sp-form-footer">
          {canManageCompany ? (
            <button type="button" className="sp-btn sp-btn-ghost sp-form-footer__danger" onClick={openDeleteModal}>Excluir</button>
          ) : (
            <button type="button" className="sp-btn sp-btn-ghost sp-form-footer__danger" onClick={goBack}>Voltar</button>
          )}
          <div className="sp-form-footer__actions">
            {canManageCompany && (
              <button type="submit" className="sp-btn sp-btn-accent" disabled={saving}>{saving ? 'Salvando...' : (isEdit ? 'Salvar alterações' : 'Salvar empresa')}</button>
            )}
          </div>
        </div>
      </form>

      <Modal open={confirmDeleteOpen} onClose={() => !deleting && setConfirmDeleteOpen(false)} width={520}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Excluir empresa</div>
            <div className="sp-modal__head-desc">A ação não pode ser desfeita.</div>
          </div>
          <button type="button" onClick={() => !deleting && setConfirmDeleteOpen(false)} className="sp-btn sp-btn-ghost sp-modal__close" disabled={deleting}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--stack-lg">
          <div className="sp-sidebar-card__muted">
            Todos os dados da empresa <strong className="sp-text-strong">{company?.name || 'selecionada'}</strong> serão excluídos definitivamente.
          </div>
          <div className="sp-delete-banner">
            Confirme sua senha de Admin para continuar.
          </div>
          <div>
            <label className="sp-caps sp-label">Senha do usuário Admin</label>
            <input
              className="sp-input"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Digite sua senha"
              disabled={deleting}
            />
          </div>
          <div className="sp-modal-actions">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>Cancelar</button>
            <button type="button" className="sp-btn sp-btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
              {deleting ? 'Excluindo...' : 'Sim, excluir empresa'}
            </button>
          </div>
        </div>
      </Modal>
    </PShell>
  );
}
